import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { CategoryEditor } from "@ui/components/category/categoryEditor";
import { PrimaryBold } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { CourseStatusLabel } from "@ui/components/course/courseStatusLabel";
import { EyeIcon } from "@ui/components/icons";
import { RichTextInput } from "@ui/components/inputs/draft/richTextInput";
import { stack } from "@ui/components/layout";
import { LevelEditor } from "@ui/components/levelEditor";
import { ContentShareroomEditor } from "@ui/components/lms/contentShareroomEditor";
import { PublisherSelector } from "@ui/components/lms/publisherSelector";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { theme } from "@ui/styles/theme";
import { gap, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { BannerEditor } from "./bannerEditor";
import { useEditorController } from "@ui/pages/lms/courseEditorPage/courseEditorController/editorControllerContext";
import { DurationEditor } from "./durationEditor";
import { IntroductionEditor } from "./introductionEditor";
import { LanguageEditor } from "@ui/pages/lms/courseEditorPage/sectionEditor/languageEditor";

interface CourseDetailsEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	courseId?: string;
	loading: boolean;
	onSaveCourse: (track?: "track", toast?: "toast", redirect?: "course" | "editor") => void;
}
export const CourseDetailsEditor: React.FC<CourseDetailsEditorProps> = ({
	courseId,
	loading,
	onSaveCourse,
	...htmlProps
}) => {
	const { courseService } = useServices();
	const controller = useEditorController();
	const course = useObservable(controller.course);
	const askDeleteConfirmation = useConfirmation(
		I18n.t("course.delete.title"),
		I18n.t("delete"),
		I18n.t("course.delete.message")
	);
	const history = useHistory();

	return (
		<Container {...htmlProps}>
			<StyledCourseStatusLabel status={course.status} />
			<DetailsBlock>
				<PrimaryBold>{I18n.t("course.editor.details.settings")}</PrimaryBold>
				<CategoryEditor
					category={course.category}
					subCategory={course.subCategory}
					setCategory={(c) => controller.setCategory(c)}
					setSubCategory={(c) => controller.setSubCategory(c)}
					label={I18n.t("category.placeholder.course")}
				/>
				<DurationEditor />
				<LanguageEditor />
				<LevelEditor
					value={course.level}
					onChange={(value) => controller.setLevel(value)}
					placeholder={I18n.t("course.editor.level.placeholder")}
				/>
			</DetailsBlock>
			<PresentationBlock>
				<PrimaryBold>{I18n.t("course.editor.details.presentation")}</PrimaryBold>
				<BannerEditor />
				<IntroductionEditor />
				<DescriptionEditor
					placeholder={I18n.t("course.editor.description.placeholder") + "*"}
					value={course.description}
					onChange={(value) => controller.setDescription(value)}
				/>
			</PresentationBlock>
			<ContentShareroomEditor
				draft={course}
				onToggleWorkspace={(id) => controller.toggleWorkspace(id)}
				onTogglePrivacy={(toggled) => controller.setVisibility(toggled ? "PRIVATE" : "PUBLIC")}
			/>
			<PublicationBlock>
				<PrimaryBold>{I18n.t("course.editor.details.publication")}</PrimaryBold>
				<LoadingButton
					buttonType="secondary"
					loading={loading}
					disabled={course.status === "PUBLISHED" ? !controller.isValidForPublication() : !controller.isValidForDraft()}
					onClick={() => onSaveCourse("track", "toast", "editor")}
				>
					{I18n.t(course.status === "PUBLISHED" ? "course.editor.save.published" : "course.editor.save.draft")}
				</LoadingButton>
				{course.status !== "PUBLISHED" && (
					<LoadingButton
						buttonType="secondary"
						loading={loading}
						disabled={!controller.isValidForDraft()}
						onClick={() => onSaveCourse("track", "toast", "course")}
					>
						<EyeIcon color={controller.isValidForDraft() ? theme.colors.red : theme.colors.white} />
						{I18n.t("article.editor.save.preview")}
					</LoadingButton>
				)}
				<PublisherSelector
					initialContent={course}
					onPublisherChange={(publisher) => {
						controller.setPublisher(publisher);
					}}
					onKeepOwnershipChange={(keepOwnership) => {
						controller.setOwnership(keepOwnership);
					}}
				/>
				<LoadingButton
					loading={loading}
					disabled={!controller.isValidForPublication()}
					onClick={
						course.status === "PUBLISHED"
							? () => onSaveCourse("track", "toast", "course")
							: () => controller.setStatus("PUBLISHED")
					}
				>
					{I18n.t("course.editor.publish")}
				</LoadingButton>
			</PublicationBlock>
			<TertiaryActions>
				{course.status === "PUBLISHED" && (
					<TertiaryButton onClick={() => controller.setStatus("ARCHIVED")}>
						{I18n.t("course.editor.archive")}
					</TertiaryButton>
				)}
				{!!courseId && (
					<TertiaryButton
						onClick={() =>
							askDeleteConfirmation(async () => {
								await courseService.deleteCourse(courseId);
								history.push(Path.Root);
								createToast(I18n.t("course.toast.delete.title"), I18n.t("course.toast.delete.message"), "success");
							})
						}
					>
						{I18n.t("course.editor.delete")}
					</TertiaryButton>
				)}
			</TertiaryActions>
		</Container>
	);
};

const Container = styled.div`
	${stack(20, "flex-start", "stretch")};
`;

const PublicationBlock = styled(RoundedBlock)`
	padding: 20px;
	${gap(20)};
`;

const DetailsBlock = styled(RoundedBlock)`
	padding: 20px;
	${gap(20)};
`;

const PresentationBlock = styled(RoundedBlock)`
	padding: 20px;
	${gap(20)};
`;

const DescriptionEditor = styled(RichTextInput)`
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
	min-height: 100px;
	margin-top: 20px;
	cursor: text;

	.public-DraftEditor-content,
	.public-DraftEditorPlaceholder-root {
		padding: 20px;
	}
	.public-DraftEditorPlaceholder-root {
		opacity: 0.8;
	}
`;

const TertiaryActions = styled.div`
	align-self: center;
	@media ${mobile} {
		margin-top: 20px;
	}
	${gap(10)};

	> button {
		margin: 0 auto;
		text-decoration: none;
		${({ theme }) => theme.textStyles.label};
		font-size: 0.9375rem;
	}
`;

const StyledCourseStatusLabel = styled(CourseStatusLabel)`
	padding: 15px 10px;
	font-size: 0.9375rem;
	border-radius: 13px;
`;
